import { Component, input, signal } from '@angular/core';
import { Fieldset } from 'primeng/fieldset';
import { Tooltip } from 'primeng/tooltip';
import { LEGAL_TEMPLATE_VARIABLES } from '@/app/models/legal-documents';

/**
 * Reference card for the Liquid variables the backend substitutes into legal
 * documents at serve time (see platform-server GetSiteContentOperation /
 * TemplateHelper.CreateLegalGlobals). Shown next to every legal-document
 * editor so authors know what they can interpolate. Clicking a variable
 * copies the ready-to-paste `{{ Token }}` expression.
 */
@Component({
    selector: 'app-legal-template-variables-hint',
    standalone: true,
    imports: [Fieldset, Tooltip],
    template: `
        <p-fieldset
            legend="Template variables"
            [toggleable]="true"
            [collapsed]="collapsed()"
        >
            <p class="mt-0 mb-3 text-sm text-muted-color">
                Documents are rendered per realm with
                <a
                    href="https://shopify.github.io/liquid/"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="text-primary hover:underline"
                    >Liquid</a
                >
                syntax. These variables are available in both the title and the
                content (click to copy):
            </p>
            <table class="w-full text-sm">
                <tbody>
                    @for (v of variables; track v.token) {
                        <tr
                            class="border-b border-surface-200 dark:border-surface-700 last:border-0"
                        >
                            <td class="py-1 pr-4 whitespace-nowrap">
                                <button
                                    type="button"
                                    class="inline-flex items-center gap-2 cursor-pointer hover:text-primary"
                                    pTooltip="Click to copy"
                                    tooltipPosition="left"
                                    (click)="copy(v.token)"
                                >
                                    <code>{{ '{{' + v.token + '}}' }}</code>
                                    @if (copied() === v.token) {
                                        <i
                                            class="pi pi-check text-green-500 text-xs"
                                        ></i>
                                    } @else {
                                        <i
                                            class="pi pi-copy text-muted-color text-xs"
                                        ></i>
                                    }
                                </button>
                            </td>
                            <td class="py-1 text-muted-color">
                                {{ v.description }}
                            </td>
                        </tr>
                    }
                </tbody>
            </table>
            <p class="mt-3 mb-0 text-sm text-muted-color">
                Values are HTML-escaped by default. Append
                <code>|raw</code> (last in the expression, e.g.
                <code>{{ '{{Company.Name|raw}}' }}</code>) to insert the value
                without escaping. Avoid spaces inside the braces — the rich-text
                editor turns them into non-breaking spaces, which break
                rendering.
            </p>
        </p-fieldset>
    `,
})
export class LegalTemplateVariablesHint {
    /** Collapsed initially; the edit dialog shows it expanded by default. */
    readonly collapsed = input(false);

    protected readonly variables = LEGAL_TEMPLATE_VARIABLES;

    /** Token whose copy confirmation is currently shown. */
    protected readonly copied = signal<string | null>(null);
    private copyTimer: number | null = null;

    protected async copy(token: string): Promise<void> {
        try {
            // No spaces inside the braces: Quill encodes them as &nbsp;,
            // which the server-side Liquid parser does not strip.
            await navigator.clipboard.writeText(`{{${token}}}`);
        } catch {
            return; // clipboard unavailable (permissions / insecure context)
        }
        this.copied.set(token);
        if (this.copyTimer !== null) {
            window.clearTimeout(this.copyTimer);
        }
        this.copyTimer = window.setTimeout(() => this.copied.set(null), 1500);
    }
}
